import React, { useEffect, useState } from 'react';

import {
  Stack,
  Text,
  Button,
  Group,
  Alert,
  Loader
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import type { Client } from './types';
import { PostJSON } from '../../utils/request';

interface QuickAssignAgentProps {
  client: Client;
  onSuccess: () => void;
  onCancel: () => void;
}

interface PhoneNumber {
  sid: string;
  phoneNumber: string;
  friendlyName: string;
}

interface PhoneNumbersResponse {
  success: boolean;
  phoneNumbers: PhoneNumber[];
  count: number;
}

interface PurchaseNumberResponse {
  success: boolean;
  phoneNumber: PhoneNumber;
}

type Status = 'loading' | 'assigning' | 'no_numbers' | 'purchasing' | 'error';

const QuickAssignAgent: React.FC<QuickAssignAgentProps> = ({ client, onSuccess, onCancel }) => {
  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const assignAgent = async (sid: string) => {
    setStatus('assigning');
    await PostJSON('/api/agent/add', {
      clientId: client.id,
      sid
    });

    notifications.show({
      title: 'Success',
      message: `Phono Assistant has been added to ${client.firstName} ${client.lastName}`,
      color: 'green'
    });

    onSuccess();
  };

  const handlePurchaseAndAssign = async () => {
    try {
      setStatus('purchasing');

      const purchaseResult = await PostJSON<PurchaseNumberResponse>('/api/agent/purchase_number');

      await assignAgent(purchaseResult.phoneNumber.sid);
    } catch (error) {
      console.error('Error purchasing number:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to purchase a new phone number. Please try again.');
      setStatus('error');
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        setStatus('loading');

        const data = await PostJSON<PhoneNumbersResponse>('/api/agent/list_numbers');

        if (data.phoneNumbers && data.phoneNumbers.length > 0) {
          await assignAgent(data.phoneNumbers[0].sid);
        } else {
          setStatus('no_numbers');
        }
      } catch (error) {
        console.error('Error in quick assign:', error);
        setErrorMessage(error instanceof Error ? error.message : 'Failed to assign Phono Assistant. Please try again.');
        setStatus('error');
      }
    };

    void run();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'loading') {
    return (
      <Stack align="center" gap="md" p="lg">
        <Loader />
        <Text>Checking available phone numbers...</Text>
      </Stack>
    );
  }

  if (status === 'assigning') {
    return (
      <Stack align="center" gap="md" p="lg">
        <Loader />
        <Text>Assigning phone number to {client.firstName} {client.lastName}...</Text>
      </Stack>
    );
  }

  if (status === 'purchasing') {
    return (
      <Stack align="center" gap="md" p="lg">
        <Loader />
        <Text>Purchasing new phone number...</Text>
      </Stack>
    );
  }

  if (status === 'error') {
    return (
      <Stack gap="md" p="lg">
        <Alert color="red" title="Error">
          {errorMessage}
        </Alert>
        <Group justify="flex-end">
          <Button variant="outline" onClick={onCancel}>
            Close
          </Button>
        </Group>
      </Stack>
    );
  }

  // status === 'no_numbers'
  return (
    <Stack gap="md" p="lg">
      <Alert color="yellow" title="No Phone Numbers Available">
        There are no available phone numbers. Would you like to purchase a new one?
      </Alert>
      <Group justify="flex-end">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => void handlePurchaseAndAssign()}>
          Purchase Number
        </Button>
      </Group>
    </Stack>
  );
};

export default QuickAssignAgent;
